import React, { useEffect, useState, useCallback, memo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  useTheme,
  useMediaQuery,
  Container,
  Breadcrumbs,
  Fade,
  Card,
  CardContent,
  Alert,
  ListItemIcon,
  Chip,
  CircularProgress,
  InputAdornment,
  Tooltip,
  FormControlLabel,
  ListItemButton,
  Snackbar
} from "@mui/material";

// アイコン
import DeleteIcon from "@mui/icons-material/Delete";
import HomeIcon from "@mui/icons-material/Home";
import GroupIcon from "@mui/icons-material/Group";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import WarningIcon from "@mui/icons-material/Warning";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ErrorIcon from "@mui/icons-material/Error";

import Header from "../../components/Header";

// メモ化されたClientItemコンポーネント
const ClientItem = memo(({ client, onDelete }) => (
  <ListItem
    divider
    secondaryAction={
      <Tooltip title="グループから削除">
        <IconButton
          edge="end"
          onClick={() => onDelete(client.id)}
          color="error"
          size="small"
          sx={{ minHeight: { xs: 48, sm: 40 } }} // タッチしやすい高さ
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    }
    sx={{ py: 1.5 }}
  >
    <ListItemIcon>
      <PersonIcon color="primary" />
    </ListItemIcon>
    <ListItemText primary={client.name} primaryTypographyProps={{ fontWeight: "medium" }} />
  </ListItem>
));

// 修正版 - ListItemButton を使用した AddClientItem コンポーネント
const AddClientItem = memo(({ client, isSelected, onToggle }) => {
  const handleToggle = useCallback(() => {
    onToggle(client.id);
  }, [client.id, onToggle]);

  return (
    <ListItemButton
      onClick={handleToggle}
      dense
      sx={{
        py: 1,
        minHeight: { xs: 56, sm: 48 }, // タッチしやすい高さ
        bgcolor: isSelected ? "primary.light" : "transparent",
        "&:hover": {
          bgcolor: isSelected ? "primary.dark" : "rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <ListItemIcon>
        <Checkbox
          edge="start"
          checked={isSelected}
          onChange={handleToggle}
          onClick={(e) => {
            // イベントバブリングを停止し、二重クリック防止
            e.stopPropagation();
          }}
          disableRipple
          icon={<CheckBoxOutlineBlankIcon />}
          checkedIcon={<CheckBoxIcon />}
          color="primary"
          sx={{ mr: 1 }}
        />
      </ListItemIcon>
      <ListItemText primary={client.name} />
    </ListItemButton>
  );
});

// すべて選択用のコンポーネント
const SelectAllItem = memo(({ isAllSelected, onToggleAll, clientCount }) => {
  return (
    <ListItemButton
      onClick={onToggleAll}
      dense
      sx={{
        py: 1,
        minHeight: { xs: 56, sm: 48 }, // タッチしやすい高さ
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: isAllSelected ? "primary.light" : "transparent",
        "&:hover": {
          bgcolor: isAllSelected ? "primary.dark" : "rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <ListItemIcon>
        <Checkbox
          edge="start"
          checked={isAllSelected}
          onChange={onToggleAll}
          onClick={(e) => {
            e.stopPropagation();
          }}
          disableRipple
          icon={<CheckBoxOutlineBlankIcon />}
          checkedIcon={<CheckBoxIcon />}
          color="primary"
          sx={{ mr: 1 }}
        />
      </ListItemIcon>
      <ListItemText primary={`すべて選択 (${clientCount}件)`} />
    </ListItemButton>
  );
});

const GroupEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // エラー通知用の状態
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // 状態管理を統合
  const [state, setState] = useState({
    groupName: "",
    clients: [],
    unassignedClients: [],
    filteredUnassignedClients: [],
    selectedClientIds: [],
    loading: true,
    error: null,
    openAddDialog: false,
    openEditDialog: false,
    newName: "",
    nameError: "",
    searchText: "",
  });

  // 状態から分割代入でアクセス
  const {
    groupName,
    clients,
    unassignedClients,
    filteredUnassignedClients,
    selectedClientIds,
    loading,
    error,
    openAddDialog,
    openEditDialog,
    newName,
    nameError,
    searchText,
  } = state;

  /**
   * 状態の部分更新ヘルパー
   * オブジェクト or コールバック関数の両方に対応
   */
  const updateState = useCallback((updates) => {
    setState((prev) => {
      const patch = typeof updates === "function" ? updates(prev) : updates;
      return { ...prev, ...patch };
    });
  }, []);

  // グループ情報の取得
  const fetchGroupInfo = useCallback(async () => {
    try {
      updateState({ loading: true });

      const res = await fetch(`/api/client_groups/${id}/clients`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
      });

      if (!res.ok) {
        if (res.status === 404) {
          alert("このグループは削除された可能性があります。グループ一覧に戻ります。");
          navigate("/group-list");
        } else {
          throw new Error("グループ情報の取得に失敗しました");
        }
        return;
      }

      const data = await res.json();
      updateState({
        groupName: data.groupName,
        clients: data.clients,
        loading: false,
      });
    } catch (err) {
      updateState({
        error: "グループ情報の取得に失敗しました",
        loading: false,
      });
      alert("グループ情報の取得中にエラーが発生しました。");
      navigate("/group-list");
    }
  }, [id, navigate, updateState]);

  // 初期データ取得
  useEffect(() => {
    fetchGroupInfo();
  }, [fetchGroupInfo]);

  // グループ名更新
  const handleUpdateGroupName = useCallback(async () => {
    try {
      updateState({ loading: true });

      const res = await fetch(`/api/client_groups/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
        body: JSON.stringify({ name: newName }),
      });

      if (!res.ok) {
        if (res.status === 404) {
          alert("このグループは削除されている可能性があります。グループ一覧に戻ります。");
          navigate("/group-list");
        } else {
          const text = await res.text();
          throw new Error(text);
        }
        return;
      }

      updateState({
        groupName: newName,
        openEditDialog: false,
        loading: false,
      });
    } catch (err) {
      updateState({
        nameError: err.message,
        loading: false,
      });
    }
  }, [id, newName, navigate, updateState]);

  // 顧客削除処理
  const handleDeleteClient = useCallback(
    (clientId) => {
      if (!window.confirm("この顧客をグループから削除しますか？")) return;

      updateState({ loading: true });

      fetch(`/api/client_groups/${id}/remove_client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
        body: JSON.stringify({ clientId }),
      })
        .then(() => {
          updateState({
            clients: clients.filter((c) => c.id !== clientId),
            loading: false,
          });
        })
        .catch(() => {
          updateState({ loading: false });
          alert("顧客の削除に失敗しました");
        });
    },
    [id, clients, updateState]
  );

  // グループ削除処理
  const handleDeleteGroup = useCallback(() => {
    if (!window.confirm("このグループを削除しますか？")) return;

    updateState({ loading: true });

    fetch(`/api/client_groups/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
    })
      .then(() => navigate("/group-list"))
      .catch(() => {
        updateState({ loading: false });
        alert("グループの削除に失敗しました");
      });
  }, [id, navigate, updateState]);

  // 顧客追加ダイアログを開く
  const openAddClientDialog = useCallback(async () => {
    try {
      updateState({ loading: true });

      const res = await fetch(`/api/client_groups/${id}/unassigned_clients`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
      });

      if (!res.ok) {
        throw new Error(`エラー: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();

      // 空のデータチェック
      if (!Array.isArray(data)) {
        throw new Error("データ形式が不正です");
      }

      updateState({
        unassignedClients: data,
        filteredUnassignedClients: data,
        selectedClientIds: [],
        openAddDialog: true,
        searchText: "",
        loading: false,
      });
    } catch (err) {
      updateState({ loading: false });
      setSnackbarMessage(`未割り当ての顧客情報の取得に失敗しました: ${err.message}`);
      setSnackbarOpen(true);
    }
  }, [id, updateState]);

  // 顧客を選択/解除
  const toggleClient = useCallback(
    (clientId) => {
      updateState((prev) => ({
        selectedClientIds: prev.selectedClientIds.includes(clientId)
          ? prev.selectedClientIds.filter((id) => id !== clientId)
          : [...prev.selectedClientIds, clientId],
      }));
    },
    [updateState]
  );

  // 全て選択/解除
  const toggleAllClients = useCallback(() => {
    updateState((prev) => {
      // 現在表示されている顧客が全て選択されているか確認
      const allSelected = prev.filteredUnassignedClients.every((client) =>
        prev.selectedClientIds.includes(client.id)
      );

      if (allSelected) {
        // 全て選択されている場合は、現在表示中の顧客を全て解除
        const displayedIds = prev.filteredUnassignedClients.map((client) => client.id);
        return {
          selectedClientIds: prev.selectedClientIds.filter((id) => !displayedIds.includes(id)),
        };
      }

      // そうでない場合は、表示中の顧客を全て選択（既存の選択も維持）
      const newSelectedIds = [...prev.selectedClientIds];
      prev.filteredUnassignedClients.forEach((client) => {
        if (!newSelectedIds.includes(client.id)) newSelectedIds.push(client.id);
      });
      return { selectedClientIds: newSelectedIds };
    });
  }, [updateState]);

  // 顧客追加処理
  const handleAddClients = useCallback(async () => {
    const confirm = window.confirm("選択した顧客をグループに追加しますか？");
    if (!confirm) return;

    updateState({ loading: true });

    try {
      const res = await fetch(`/api/client_groups/${id}/add_clients`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
        body: JSON.stringify({ clientIds: selectedClientIds }),
      });

      if (!res.ok) {
        throw new Error(`エラー: ${res.status} ${res.statusText}`);
      }

      updateState({ openAddDialog: false });
      fetchGroupInfo();
    } catch (err) {
      updateState({ loading: false });
      setSnackbarMessage(`顧客の追加に失敗しました: ${err.message}`);
      setSnackbarOpen(true);
    }
  }, [id, selectedClientIds, updateState, fetchGroupInfo]);

  // グループ名編集ダイアログを開く
  const openEditNameDialog = useCallback(() => {
    updateState({
      newName: groupName,
      nameError: "",
      openEditDialog: true,
    });
  }, [groupName, updateState]);

  // 検索テキスト変更時の処理
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      updateState({
        searchText: value,
        filteredUnassignedClients: value
          ? unassignedClients.filter((client) => client.name.toLowerCase().includes(value.toLowerCase()))
          : unassignedClients,
      });
    },
    [unassignedClients, updateState]
  );

  // グループ名変更時のバリデーション
  const handleGroupNameChange = useCallback(
    (e) => {
      const value = e.target.value;
      let error = "";

      if (/[^a-zA-Z0-9一-龠ぁ-んァ-ンー・「」ａ-ｚＡ-Ｚ０-９\s]/.test(value)) {
        error = "使用できない文字が含まれています";
      } else if (value.length > 20) {
        error = "20文字以内で入力してください";
      } else if (!value.trim()) {
        error = "グループ名を入力してください";
      }

      updateState({ newName: value, nameError: error });
    },
    [updateState]
  );

  // ダイアログを閉じる
  const closeAddDialog = useCallback(() => {
    updateState({ openAddDialog: false });
  }, [updateState]);

  // 全選択状態を確認
  const areAllSelected =
    filteredUnassignedClients.length > 0 &&
    filteredUnassignedClients.every((client) => selectedClientIds.includes(client.id));

  // スナックバーを閉じる
  const handleCloseSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  return (
    <>
      <Header />
      <Container 
        maxWidth="md" 
        sx={{ 
          mt: 4, 
          mb: 6, 
          px: { xs: 2, sm: 3 },
          // PWAボトムナビゲーション対応：安全マージンを確保
          pb: { xs: 12, sm: 6 }, // モバイルは12（約96px）、デスクトップは6
          // 最小高さを確保してスクロール可能にする
          minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
        }}
      >
        {/* パンくずリスト */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            ホーム
          </Typography>
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/group-list')}
          >
            <GroupsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            グループ一覧
          </Typography>
          <Typography
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}
            color="text.primary"
          >
            <GroupIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            グループ編集
          </Typography>
        </Breadcrumbs>

        {loading && !clients.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        ) : (
          <Fade in={true}>
            <Box>
              {/* ヘッダーセクション */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  グループの編集
                </Typography>
                <Typography color="text.secondary">
                  グループ名の変更や所属顧客の管理ができます
                </Typography>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* グループ名セクション */}
              <Card 
                elevation={2} 
                sx={{ 
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 4
                }}
              >
                <Box 
                  sx={{ 
                    px: 3, 
                    py: 2, 
                    bgcolor: 'primary.light',
                    color: 'primary.dark',
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <GroupIcon />
                  <Typography variant="subtitle1" fontWeight="medium">
                    グループ基本情報
                  </Typography>
                </Box>

                <CardContent sx={{ p: 3 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2
                    }}
                  >
                    <TextField
                      label="グループ名"
                      value={groupName}
                      fullWidth
                      InputProps={{ 
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <GroupIcon color="primary" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-root': {
                          minHeight: { xs: 56, sm: 48 } // タッチしやすい高さ
                        }
                      }}
                    />
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1,
                      width: { xs: '100%', sm: 'auto' }
                    }}>
                      <Button 
                        variant="contained" 
                        startIcon={<EditIcon />}
                        onClick={openEditNameDialog}
                        fullWidth={isMobile}
                        sx={{ minHeight: { xs: 48, sm: 40 } }}
                      >
                        名前を更新
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteGroup}
                        fullWidth={isMobile}
                        sx={{ minHeight: { xs: 48, sm: 40 } }}
                      >
                        削除
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* 所属顧客セクション */}
              <Card 
                elevation={2} 
                sx={{ 
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 4
                }}
              >
                <Box 
                  sx={{ 
                    px: 3, 
                    py: 2, 
                    bgcolor: 'primary.light',
                    color: 'primary.dark',
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon />
                    <Typography variant="subtitle1" fontWeight="medium">
                      所属顧客一覧
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${clients.length}件`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </Box>

                <CardContent sx={{ p: 0 }}>
                  {clients.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary" gutterBottom>
                        顧客が登録されていません
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openAddClientDialog}
                        sx={{ 
                          mt: 1,
                          minHeight: { xs: 48, sm: 40 }
                        }}
                      >
                        顧客を追加
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <List sx={{ p: 0 }}>
                        {clients.map((client) => (
                          <ClientItem 
                            key={client.id} 
                            client={client} 
                            onDelete={handleDeleteClient} 
                          />
                        ))}
                      </List>
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Button 
                          variant="contained" 
                          startIcon={<AddIcon />} 
                          onClick={openAddClientDialog}
                          sx={{ minHeight: { xs: 48, sm: 40 } }}
                        >
                          顧客を追加
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* 戻るボタン */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/group-list")}
                  size="large"
                  sx={{ 
                    minWidth: 200,
                    minHeight: { xs: 48, sm: 40 }
                  }}
                >
                  グループ一覧に戻る
                </Button>
              </Box>
            </Box>
          </Fade>
        )}
      </Container>

      {/* 顧客追加ダイアログ */}
      <Dialog 
        open={openAddDialog} 
        onClose={closeAddDialog} 
        fullScreen={isMobile} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              顧客を追加
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={closeAddDialog}
            aria-label="閉じる"
            sx={{ minHeight: { xs: 48, sm: 40 } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 0 }}>
          {/* 検索フィールド */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              placeholder="顧客名で検索"
              value={searchText}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
              sx={{ 
                mb: 1,
                '& .MuiInputBase-root': {
                  minHeight: { xs: 56, sm: 48 }
                }
              }}
            />
          </Box>
          
          <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredUnassignedClients.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {searchText ? "検索条件に一致する顧客がありません" : "追加できる顧客がありません"}
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {/* すべて選択オプション */}
                <SelectAllItem 
                  isAllSelected={areAllSelected}
                  onToggleAll={toggleAllClients}
                  clientCount={filteredUnassignedClients.length}
                />
                
                {/* 顧客リスト */}
                {filteredUnassignedClients.map((client) => (
                  <AddClientItem
                    key={client.id}
                    client={client}
                    isSelected={selectedClientIds.includes(client.id)}
                    onToggle={toggleClient}
                  />
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          position: 'sticky', 
          bottom: 0, 
          backgroundColor: 'white', 
          zIndex: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          py: 1.5,
          px: 2
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            width: '100%'
          }}>
            <Typography variant="body2" color="text.secondary">
              {selectedClientIds.length}件選択中
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                onClick={closeAddDialog}
                variant="outlined"
                sx={{ minHeight: { xs: 48, sm: 40 } }}
              >
                キャンセル
              </Button>
              <Button
                onClick={handleAddClients}
                variant="contained"
                disabled={selectedClientIds.length === 0}
                startIcon={<AddIcon />}
                sx={{ minHeight: { xs: 48, sm: 40 } }}
              >
                追加
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* グループ名編集ダイアログ */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => updateState({ openEditDialog: false })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              グループ名を変更
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={() => updateState({ openEditDialog: false })}
            aria-label="閉じる"
            sx={{ minHeight: { xs: 48, sm: 40 } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="新しいグループ名"
            value={newName}
            onChange={handleGroupNameChange}
            error={!!nameError}
            helperText={nameError || `${newName.length}/20文字`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GroupIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: { xs: 56, sm: 48 } // タッチしやすい高さ
              }
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => updateState({ openEditDialog: false })}
            variant="outlined"
            sx={{ minHeight: { xs: 48, sm: 40 } }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleUpdateGroupName}
            variant="contained"
            disabled={!!nameError || newName.trim() === ""}
            startIcon={<EditIcon />}
            sx={{ minHeight: { xs: 48, sm: 40 } }}
          >
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* エラー通知用スナックバー */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon color="error" />
            {snackbarMessage}
          </Box>
        }
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleCloseSnackbar}
            sx={{ minHeight: { xs: 48, sm: 40 } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </>
  );
};

export default GroupEdit;